import * as yup from 'yup';
import { validPrefix } from '../utils/phoneNumber';
import { validEmail, validPhoneCharacters } from '../utils/regex';

export type BuyingType = 'BUYING' | 'SELLING' | 'BUYING_AND_SELLING';
export type LegalFeeBuyerType = 'SOLE' | 'JOINT';
export type BuyerType = 'FIRST_TIME_BUYER' | 'HOME_MOVER' | 'BUY_TO_LET_INVESTOR' | 'ADDITIONAL_HOME_BUYER';
export type BooleanKey = 'TRUE' | 'FALSE';

export type LegalFeesRequest = {
  buyingType?: BuyingType;
  buyerType?: LegalFeeBuyerType;
  existingMortgage?: boolean;
  newMortgage?: boolean;
  expectedSalePrice?: number;
  purchasePrice?: number;
  stampDutyType?: BuyerType;
  ukResident?: boolean;
  recaptcha?: string | null;
  name?: string | null;
  telephone?: string | null;
  email?: string | null;
  propertyId?: number;
  source?: string;
};

export type OutlayType = 'OUTLAY' | 'STAMP_DUTY' | 'PROFESSIONAL';

type LegalFeesItemResponse = {
  label: string;
  price: number;
};

export type LegalFeesResponse = {
  items: Record<OutlayType, LegalFeesItemResponse[]>;
  total: number;
  outlaysTotal: number;
};

export type LegalFeesContact = {
  recaptcha?: string | '';
  name?: string | '';
  prefix?: string | '';
  telephone?: string | '';
  email?: string | '';
};

export type LegalFeesContactApp = Omit<LegalFeesContact, 'prefix' | 'recaptcha'>;

type LegalFeesCalculateForm = Omit<LegalFeesRequest, 'name' | 'email' | 'telephone'>;

export type LegalFeesFormValue = {
  [K in keyof LegalFeesCalculateForm]:
    | (LegalFeesCalculateForm[K] extends number | undefined
        ? string
        : LegalFeesCalculateForm[K] extends boolean | undefined
        ? BooleanKey
        : LegalFeesCalculateForm[K])
    | '';
};

type FormOptionValue<T = string> = {
  key: T;
  label: string;
};

export type FormOptionsData<T = string> = {
  label: string;
  property: keyof LegalFeesFormValue;
  values: FormOptionValue<T>[];
  bool?: boolean;
};

export const buyingTypeOptions: FormOptionsData<BuyingType> = {
  label: 'Are you buying, selling or both?',
  property: 'buyingType',
  values: [
    { key: 'BUYING', label: 'Buying' },
    { key: 'SELLING', label: 'Selling' },
    { key: 'BUYING_AND_SELLING', label: 'Buying & Selling' },
  ],
};

export const buyerTypeOptions: FormOptionsData<LegalFeeBuyerType> = {
  label: 'Are you buying by yourself or with another person?',
  property: 'buyerType',
  values: [
    { key: 'SOLE', label: 'Sole name' },
    { key: 'JOINT', label: 'Joint names' },
  ],
};

export const sellingTypeOptions: FormOptionsData<LegalFeeBuyerType> = {
  ...buyerTypeOptions,
  label: 'Are you selling by yourself or with another person?',
};

const booleanValue: FormOptionValue<BooleanKey>[] = [
  { key: 'TRUE', label: 'Yes' },
  { key: 'FALSE', label: 'No' },
];

export const existingMortgageOptions: FormOptionsData<BooleanKey> = {
  label: 'Do you have an existing mortgage on the property you are selling?',
  property: 'existingMortgage',
  values: booleanValue,
  bool: true,
};

export const newMortgageOptions: FormOptionsData<BooleanKey> = {
  label: 'Are you obtaining a new mortgage on the property you are buying?',
  property: 'newMortgage',
  values: booleanValue,
  bool: true,
};

export const stampDutyOptions: FormOptionsData<BuyerType> = {
  label: 'To calculate the correct stamp duty, can you tell us what type of buyer you are?',
  property: 'stampDutyType',
  values: [
    { key: 'FIRST_TIME_BUYER', label: 'First Time Buyer' },
    { key: 'HOME_MOVER', label: 'Home Mover' },
    { key: 'BUY_TO_LET_INVESTOR', label: 'Buy-to-Let Investor' },
    { key: 'ADDITIONAL_HOME_BUYER', label: 'Second Home Buyer' },
  ],
};

export const buyingSellingStampDutyOptions: FormOptionsData<BuyerType> = {
  ...stampDutyOptions,
  values: stampDutyOptions.values?.filter((v) => v.key !== 'FIRST_TIME_BUYER'),
};

export const ukResidentOptions: FormOptionsData<BooleanKey> = {
  label: 'Are you a UK resident?',
  property: 'ukResident',
  values: booleanValue,
  bool: true,
};

export const LEGAL_FEES_ERR_MSG =
  'Due to the property value Millar McCall Wylie will need to provide you with a bespoke quotation. Please contact them directly using the form below.';

export const legalFeesSchema = yup.object().shape({
  buyingType: yup.string().required('Please select whether you are buying, selling or both.'),

  buyerType: yup.string().required('Please select whether you are buying in sole or joint names.'),

  existingMortgage: yup.string().when('buyingType', {
    is: (val: string) => val === 'SELLING' || val === 'BUYING_AND_SELLING',
    then: (schema) => schema.required('Please select whether you have an existing mortgage on sale.'),
  }),

  newMortgage: yup.string().when('buyingType', {
    is: (val: string) => val === 'BUYING' || val === 'BUYING_AND_SELLING',
    then: (schema) => schema.required('Please select whether you are obtaining a new mortgage on purchase.'),
  }),

  stampDutyType: yup.string().when('buyingType', {
    is: (val: string) => val === 'BUYING' || val === 'BUYING_AND_SELLING',
    then: (schema) => schema.required('Please select which buyer type you are.'),
  }),

  ukResident: yup.string().when('buyingType', {
    is: (val: string) => val === 'BUYING' || val === 'BUYING_AND_SELLING',
    then: (schema) => schema.required('Please select if you are a UK resident.'),
  }),

  expectedSalePrice: yup.string().when('buyingType', {
    is: (val: string) => val === 'SELLING' || val === 'BUYING_AND_SELLING',
    then: (schema) =>
      schema
        .required('Please enter the anticipated sale price of your existing property.')
        .test('is-valid-price', LEGAL_FEES_ERR_MSG, (value) => {
          const price = parseFloat(value?.replace(/,/g, '') || '');
          return !Number.isNaN(price) && price >= 1 && price <= 500000;
        }),
  }),

  purchasePrice: yup.string().when('buyingType', {
    is: (val: string) => val === 'BUYING' || val === 'BUYING_AND_SELLING',
    then: (schema) =>
      schema
        .required('Please enter the anticipated purchase price of the property.')
        .test('is-valid-price', LEGAL_FEES_ERR_MSG, (value) => {
          const price = parseFloat(value?.replace(/,/g, '') || '');
          return !Number.isNaN(price) && price >= 1 && price <= 500000;
        }),
  }),
});

export const legalFeesContactSchema = yup.object().shape({
  name: yup.string().trim().required('Please enter your name'),
  prefix: yup.string().trim().test(validPrefix).required('Please include a country code'),
  telephone: yup
    .string()
    .trim()
    .matches(validPhoneCharacters, 'Please enter a valid phone number')
    .required('Please enter your phone number'),
  email: yup
    .string()
    .trim()
    .matches(validEmail, 'Please enter a valid email address')
    .required('Please enter your email address'),
});

export const legalFeesContactSchemaApp = yup.object().shape({
  name: yup.string().trim().required('Please enter your name'),
  telephone: yup
    .string()
    .trim()
    .matches(validPhoneCharacters, 'Please enter a valid phone number')
    .required('Please enter your phone number'),
  email: yup
    .string()
    .trim()
    .matches(validEmail, 'Please enter a valid email address')
    .required('Please enter your email address'),
});
